/**
 * QUOTATION FILE THUNKS — operazioni sugli allegati di un preventivo.
 *
 * Scritti a mano e non dal factory: `createCrudThunks` assume una risorsa di
 * primo livello scopata sul sito (`create(groupId, …)`, `getById(id)`), mentre
 * qui ogni operazione è scopata sul preventivo padre. Vedi il docblock di
 * `quotationFileApi.ts`.
 *
 * Il prefisso degli action type è `quotationFiles` per la stessa ragione
 * dello slice: `quotationFiles/*` è già in uso e `addCase` confronta stringhe.
 * Torna al nome definitivo allo step 12 di TS-87.
 */

import { createAsyncThunk } from '@reduxjs/toolkit';
import type { Page } from '@crm/shared';
import { extractError } from '../../store/thunkHelpers';
import { quotationFileApi } from './quotationFileApi';
import type {
  QuotationFile,
  QuotationFileUploadPayload,
  QuotationFileUpdatePayload,
  QuotationFileDeletePayload,
} from './quotationFileTypes';

export const fetchQuotationFiles = createAsyncThunk<
  Page<QuotationFile>,
  number,
  { rejectValue: string }
>('quotationFiles/fetchByQuotation', async (quotationId, { rejectWithValue }) => {
  try {
    return await quotationFileApi.getByQuotation(quotationId);
  } catch (err: unknown) {
    return rejectWithValue(extractError(err));
  }
});

export const uploadQuotationFile = createAsyncThunk<
  QuotationFile,
  QuotationFileUploadPayload,
  { rejectValue: string }
>('quotationFiles/upload', async (payload, { rejectWithValue }) => {
  try {
    const { quotationId, ...input } = payload;
    return await quotationFileApi.upload(quotationId, input);
  } catch (err: unknown) {
    return rejectWithValue(extractError(err));
  }
});

export const updateQuotationFile = createAsyncThunk<
  QuotationFile,
  QuotationFileUpdatePayload,
  { rejectValue: string }
>('quotationFiles/update', async (payload, { rejectWithValue }) => {
  try {
    const { quotationId, quotationFileId, ...changes } = payload;
    return await quotationFileApi.update(quotationId, quotationFileId, changes);
  } catch (err: unknown) {
    return rejectWithValue(extractError(err));
  }
});

export const deleteQuotationFile = createAsyncThunk<
  number,
  QuotationFileDeletePayload,
  { rejectValue: string }
>('quotationFiles/delete', async ({ quotationId, quotationFileId }, { rejectWithValue }) => {
  try {
    await quotationFileApi.remove(quotationId, quotationFileId);
    return quotationFileId;
  } catch (err: unknown) {
    return rejectWithValue(extractError(err));
  }
});
